import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as api from "../api/DocumentsApi";
import type {
  HrmsDocument,
  DocumentSummary,
  DocumentCategory,
  DocumentShare,
  DocumentCheckpoint,
  AssignableEmployee,
  SimilarDocumentMatch,
  SimilarCategoryMatch,
  SharePermission,
} from "../api/DocumentsApi";
import { ContentConflictError } from "../api/DocumentsApi";
import { VerifyContext } from "../../../app/VerifyContext";

const PAGE_SIZE = 20;

interface ContentConflict {
  content: string;
  version: number;
  updated_at: string | null;
}

interface DocumentsContextValue {
  isAdmin: boolean;
  // Whether the current user can edit currentDocument's content — admin, or
  // an employee shared with "edit" permission. Computed once here rather
  // than per-component so the rule can't silently diverge between them —
  // same reasoning as SheetsContext.canEdit.
  canEdit: boolean;

  // list
  documents: DocumentSummary[];
  totalCount: number;
  totalPages: number;
  page: number;
  loading: boolean;
  error: string;
  search: string;
  categoryFilter: number | null;
  tagFilter: string | null;
  setPage: (p: number) => void;
  setSearch: (s: string) => void;
  setCategoryFilter: (id: number | null) => void;
  setTagFilter: (tag: string | null) => void;
  refresh: () => Promise<void>;

  // categories
  categories: DocumentCategory[];
  refreshCategories: () => Promise<void>;
  createCategory: (name: string, description?: string) => Promise<DocumentCategory>;
  renameCategory: (id: number, name: string) => Promise<void>;
  removeCategory: (id: number) => Promise<void>;
  checkSimilarDocuments: (title: string) => Promise<SimilarDocumentMatch[]>;
  checkSimilarCategories: (name: string) => Promise<SimilarCategoryMatch[]>;

  // create / delete
  createDocument: (data: { title: string; description?: string; category_id?: number | null; tags?: string[] }) => Promise<HrmsDocument>;
  deleteDocument: (id: number) => Promise<void>;
  importDocxCreate: (file: File, title?: string) => Promise<HrmsDocument>;

  // detail
  currentDocument: HrmsDocument | null;
  // Bumped only when currentDocument.content was replaced from OUTSIDE the
  // user's own typing (initial load, a resolved conflict, a checkpoint
  // restore) — never on a plain successful save, where the editor already
  // holds the just-saved text and remounting it would just reset the
  // cursor/scroll position for no reason. DocumentDetailPage keys the Quill
  // instance on this (plus currentDocument.id) so it remounts exactly when
  // the on-screen text needs to be forcibly replaced.
  contentEpoch: number;
  detailLoading: boolean;
  detailError: string;
  loadDocumentDetail: (id: number) => Promise<void>;
  updateDocumentMeta: (data: { title?: string; description?: string; category_id?: number | null; tags?: string[] }) => Promise<void>;
  toggleReadonly: (enabled: boolean) => Promise<void>;

  // manual save (no autosave in v1)
  isDirty: boolean;
  markDirty: (html: string) => void;
  saveStatus: "idle" | "saving" | "saved" | "error";
  saveError: string | null;
  lastSavedAt: Date | null;
  saveNow: () => Promise<boolean>;
  contentConflict: ContentConflict | null;
  resolveContentConflict: () => void; // discards local edits, adopts the server's current content — the only resolution offered in v1

  triggerExportDocx: () => Promise<void>;

  // checkpoints (manual version history, admin only)
  checkpoints: DocumentCheckpoint[];
  loadCheckpoints: () => Promise<void>;
  saveCheckpoint: (label: string | null) => Promise<void>;
  removeCheckpoint: (checkpointId: number) => Promise<void>;
  purgeCheckpoints: (olderThanDays: number) => Promise<number>;
  restoreCheckpoint: (checkpointId: number) => Promise<void>;

  // sharing (admin only)
  shares: DocumentShare[];
  assignableEmployees: AssignableEmployee[];
  loadShares: () => Promise<void>;
  addShares: (employeeIds: number[], permission: SharePermission) => Promise<void>;
  editSharePermission: (shareId: number, permission: SharePermission) => Promise<void>;
  removeShare: (shareId: number) => Promise<void>;
  loadAssignableEmployees: () => Promise<void>;
}

const DocumentsContext = createContext<DocumentsContextValue | null>(null);

export function DocumentsProvider({ children }: { children: React.ReactNode }) {
  const { superAdmin } = useContext(VerifyContext);
  const isAdmin = superAdmin;

  // ── list state ──────────────────────────────────────────────────────
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearchState] = useState("");
  const [categoryFilter, setCategoryFilterState] = useState<number | null>(null);
  const [tagFilter, setTagFilterState] = useState<string | null>(null);
  const [categories, setCategories] = useState<DocumentCategory[]>([]);

  const setSearch = useCallback((s: string) => { setSearchState(s); setPage(1); }, []);
  const setCategoryFilter = useCallback((id: number | null) => { setCategoryFilterState(id); setPage(1); }, []);
  const setTagFilter = useCallback((tag: string | null) => { setTagFilterState(tag); setPage(1); }, []);

  const reqSeq = useRef(0);

  const refresh = useCallback(async () => {
    const reqId = ++reqSeq.current;
    setLoading(true);
    setError("");
    try {
      const res = await api.listDocuments({
        search: search.trim() || undefined,
        category_id: categoryFilter ?? undefined,
        tag: tagFilter ?? undefined,
        page,
        page_size: PAGE_SIZE,
      });
      if (reqId !== reqSeq.current) return;
      setDocuments(res.items);
      setTotalCount(res.total_count);
      setTotalPages(res.total_pages);
      if (page > res.total_pages && res.total_pages > 0) setPage(res.total_pages);
    } catch {
      if (reqId === reqSeq.current) setError("Failed to load documents");
    } finally {
      if (reqId === reqSeq.current) setLoading(false);
    }
  }, [search, categoryFilter, tagFilter, page]);

  const refreshCategories = useCallback(async () => {
    try {
      setCategories(await api.listCategories());
    } catch {
      // non-fatal — filters/labels just show fewer options
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { refreshCategories(); }, [refreshCategories]);

  const createCategory = useCallback(async (name: string, description?: string) => {
    const cat = await api.createCategory({ name, description });
    await refreshCategories();
    return cat;
  }, [refreshCategories]);

  const renameCategory = useCallback(async (id: number, name: string) => {
    await api.updateCategory(id, { name });
    await refreshCategories();
  }, [refreshCategories]);

  const removeCategory = useCallback(async (id: number) => {
    await api.deleteCategory(id);
    await Promise.all([refreshCategories(), refresh()]);
  }, [refreshCategories, refresh]);

  const checkSimilarDocuments = useCallback((title: string) => api.checkSimilarDocuments(title), []);
  const checkSimilarCategories = useCallback((name: string) => api.checkSimilarCategories(name), []);

  const createDocument = useCallback(async (data: { title: string; description?: string; category_id?: number | null; tags?: string[] }) => {
    const document = await api.createDocument(data);
    await refresh();
    return document;
  }, [refresh]);

  const deleteDocument = useCallback(async (id: number) => {
    await api.deleteDocument(id);
    await refresh();
  }, [refresh]);

  const importDocxCreate = useCallback(async (file: File, title?: string) => {
    const document = await api.importDocx(file, title);
    await refresh();
    return document;
  }, [refresh]);

  // ── detail state ────────────────────────────────────────────────────
  const [currentDocument, setCurrentDocument] = useState<HrmsDocument | null>(null);
  // Read-only mode (admin toggle) locks the document for everyone, admin
  // included — that's the whole point (protects against the admin's own
  // accidental edits), same as Sheets' is_readonly.
  const canEdit = (isAdmin || currentDocument?.my_permission === "edit") && !currentDocument?.is_readonly;
  const [contentEpoch, setContentEpoch] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [shares, setShares] = useState<DocumentShare[]>([]);
  const [assignableEmployees, setAssignableEmployees] = useState<AssignableEmployee[]>([]);
  const [checkpoints, setCheckpoints] = useState<DocumentCheckpoint[]>([]);

  const currentDocumentIdRef = useRef<number | null>(null);

  // No autosave in v1 — a single pending-content ref (not React state, so a
  // rapid string of onChange events from Quill doesn't force a re-render on
  // every keystroke) holds whatever hasn't been saved yet. Only `isDirty`
  // (a boolean) is real state, since that's the only part any UI needs to
  // react to (enabling the Save button, the unsaved-changes warning).
  const pendingContentRef = useRef<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [contentConflict, setContentConflict] = useState<ContentConflict | null>(null);

  const markDirty = useCallback((html: string) => {
    pendingContentRef.current = html;
    setIsDirty(true);
  }, []);

  const loadDocumentDetail = useCallback(async (id: number) => {
    currentDocumentIdRef.current = id;
    setDetailLoading(true);
    setDetailError("");
    pendingContentRef.current = null;
    setIsDirty(false);
    setSaveStatus("idle");
    setSaveError(null);
    setContentConflict(null);
    try {
      const document = await api.getDocument(id);
      if (currentDocumentIdRef.current !== id) return;
      setCurrentDocument(document);
      setContentEpoch((e) => e + 1);
    } catch {
      if (currentDocumentIdRef.current === id) setDetailError("Failed to load document");
    } finally {
      if (currentDocumentIdRef.current === id) setDetailLoading(false);
    }
  }, []);

  const updateDocumentMeta = useCallback(async (data: { title?: string; description?: string; category_id?: number | null; tags?: string[] }) => {
    if (!currentDocument) return;
    const updated = await api.updateDocument(currentDocument.id, data);
    setCurrentDocument((prev) => (prev ? { ...prev, ...updated } : updated));
    await refresh();
  }, [currentDocument, refresh]);

  const saveNow = useCallback(async (): Promise<boolean> => {
    if (!currentDocument || pendingContentRef.current === null) return true;
    const documentId = currentDocument.id;
    const content = pendingContentRef.current;
    const version = currentDocument.version;
    setSaveStatus("saving");
    setSaveError(null);
    try {
      const updated = await api.updateContent(documentId, content, version);
      if (currentDocumentIdRef.current !== documentId) return true; // navigated away mid-save — discard the stale response
      setCurrentDocument(updated);
      // Only clear the pending edit if nothing newer landed in it while this
      // save was in flight (mirrors SheetsContext.saveNow's same guard).
      if (pendingContentRef.current === content) {
        pendingContentRef.current = null;
        setIsDirty(false);
      }
      setSaveStatus("saved");
      setLastSavedAt(new Date());
      return true;
    } catch (err) {
      if (err instanceof ContentConflictError) {
        setContentConflict(err.currentDocument);
        setSaveStatus("idle"); // the conflict banner already communicates this
        return false;
      }
      setSaveStatus("error");
      setSaveError((err as any)?.response?.data?.detail || (err as any)?.message || "Failed to save changes");
      return false;
    }
  }, [currentDocument]);

  const resolveContentConflict = useCallback(() => {
    setContentConflict((conflict) => {
      if (conflict && currentDocument) {
        setCurrentDocument({ ...currentDocument, content: conflict.content, version: conflict.version });
        setContentEpoch((e) => e + 1);
        pendingContentRef.current = null;
        setIsDirty(false);
        setSaveStatus("idle");
      }
      return null;
    });
  }, [currentDocument]);

  const toggleReadonly = useCallback(async (enabled: boolean) => {
    if (!currentDocument) return;
    const documentId = currentDocument.id;
    if (enabled && isDirty) {
      const flushed = await saveNow();
      if (!flushed) throw new Error("Could not save your pending changes — read-only mode was not enabled.");
    }
    const updated = await api.updateReadonly(documentId, enabled);
    if (currentDocumentIdRef.current !== documentId) return;
    setCurrentDocument((prev) => (prev ? { ...prev, is_readonly: updated.is_readonly } : updated));
  }, [currentDocument, isDirty, saveNow]);

  // Safety net for closing/refreshing the tab with unsaved edits. KNOWN GAP
  // (same as Sheets): in-app navigation via <Link>/navigate() and browser
  // back/forward bypass this — full coverage needs react-router's
  // useBlocker, out of scope here.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const triggerExportDocx = useCallback(async () => {
    if (!currentDocument) return;
    const blob = await api.exportDocx(currentDocument.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${currentDocument.title}.docx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [currentDocument]);

  const loadShares = useCallback(async () => {
    if (!currentDocument) return;
    setShares(await api.listShares(currentDocument.id));
  }, [currentDocument]);

  const loadAssignableEmployees = useCallback(async () => {
    setAssignableEmployees(await api.listAssignableEmployees());
  }, []);

  const loadCheckpoints = useCallback(async () => {
    if (!currentDocument) return;
    setCheckpoints(await api.listCheckpoints(currentDocument.id));
  }, [currentDocument]);

  const saveCheckpoint = useCallback(async (label: string | null) => {
    if (!currentDocument) return;
    await api.createCheckpoint(currentDocument.id, label);
    await loadCheckpoints();
  }, [currentDocument, loadCheckpoints]);

  const removeCheckpoint = useCallback(async (checkpointId: number) => {
    if (!currentDocument) return;
    await api.deleteCheckpoint(currentDocument.id, checkpointId);
    await loadCheckpoints();
  }, [currentDocument, loadCheckpoints]);

  const purgeCheckpoints = useCallback(async (olderThanDays: number): Promise<number> => {
    if (!currentDocument) return 0;
    const { deleted } = await api.purgeCheckpointsOlderThan(currentDocument.id, olderThanDays);
    await loadCheckpoints();
    return deleted;
  }, [currentDocument, loadCheckpoints]);

  // Restoring replaces the document's current content server-side, so the
  // detail view reloads from scratch afterward — same reasoning as
  // SheetsContext.restoreCheckpoint (including swallowing the refresh's own
  // errors so they don't mask a genuine restore failure).
  const restoreCheckpoint = useCallback(async (checkpointId: number) => {
    if (!currentDocument) return;
    let restoreError: unknown;
    try {
      await api.restoreCheckpoint(currentDocument.id, checkpointId);
    } catch (err) {
      restoreError = err;
    }
    try {
      await Promise.all([loadDocumentDetail(currentDocument.id), loadCheckpoints()]);
    } catch (refreshErr) {
      console.error("Failed to refresh document after a checkpoint restore attempt", refreshErr);
    }
    if (restoreError) throw restoreError;
  }, [currentDocument, loadDocumentDetail, loadCheckpoints]);

  const addShares = useCallback(async (employeeIds: number[], permission: SharePermission) => {
    if (!currentDocument || employeeIds.length === 0) return;
    await Promise.all(employeeIds.map((id) => api.shareDocument(currentDocument.id, id, permission)));
    await loadShares();
  }, [currentDocument, loadShares]);

  const editSharePermission = useCallback(async (shareId: number, permission: SharePermission) => {
    if (!currentDocument) return;
    await api.updateSharePermission(currentDocument.id, shareId, permission);
    await loadShares();
  }, [currentDocument, loadShares]);

  const removeShare = useCallback(async (shareId: number) => {
    if (!currentDocument) return;
    await api.unshareDocument(currentDocument.id, shareId);
    await loadShares();
  }, [currentDocument, loadShares]);

  const value = useMemo<DocumentsContextValue>(() => ({
    isAdmin, canEdit,
    documents, totalCount, totalPages, page, loading, error, search, categoryFilter, tagFilter,
    setPage, setSearch, setCategoryFilter, setTagFilter, refresh,
    categories, refreshCategories, createCategory, renameCategory, removeCategory,
    checkSimilarDocuments, checkSimilarCategories,
    createDocument, deleteDocument, importDocxCreate,
    currentDocument, contentEpoch, detailLoading, detailError, loadDocumentDetail, updateDocumentMeta, toggleReadonly,
    isDirty, markDirty, saveStatus, saveError, lastSavedAt, saveNow, contentConflict, resolveContentConflict,
    triggerExportDocx,
    checkpoints, loadCheckpoints, saveCheckpoint, removeCheckpoint, purgeCheckpoints, restoreCheckpoint,
    shares, assignableEmployees, loadShares, addShares, editSharePermission, removeShare, loadAssignableEmployees,
  }), [
    isAdmin, canEdit,
    documents, totalCount, totalPages, page, loading, error, search, categoryFilter, tagFilter,
    setSearch, setCategoryFilter, setTagFilter, refresh,
    categories, refreshCategories, createCategory, renameCategory, removeCategory,
    checkSimilarDocuments, checkSimilarCategories,
    createDocument, deleteDocument, importDocxCreate,
    currentDocument, contentEpoch, detailLoading, detailError, loadDocumentDetail, updateDocumentMeta, toggleReadonly,
    isDirty, markDirty, saveStatus, saveError, lastSavedAt, saveNow, contentConflict, resolveContentConflict,
    triggerExportDocx,
    checkpoints, loadCheckpoints, saveCheckpoint, removeCheckpoint, purgeCheckpoints, restoreCheckpoint,
    shares, assignableEmployees, loadShares, addShares, editSharePermission, removeShare, loadAssignableEmployees,
  ]);

  return <DocumentsContext.Provider value={value}>{children}</DocumentsContext.Provider>;
}

export function useDocuments(): DocumentsContextValue {
  const ctx = useContext(DocumentsContext);
  if (!ctx) throw new Error("useDocuments must be used within DocumentsProvider");
  return ctx;
}
